import React, { useEffect, useRef, useCallback, useState } from "react";
import { twMerge } from "tailwind-merge";

import { IconButton } from "ui/icon-button";
import { Select } from "ui/select";
import { TableLoader } from "ui/data-grid";

import { useSmsSimulatorContext } from "containers/sms-simulator/sms-simulator.context";

import { SmsSimulatorSimulationSummaryAttributes } from "containers/sms-simulator/sms-simulator.types";

import Icons from "assets";

interface AllSimulationsCardProps {
  simulations: SmsSimulatorSimulationSummaryAttributes[];
  isLoading: boolean;
  onClose: () => void;
  onSelectSimulation: (simulation: SmsSimulatorSimulationSummaryAttributes) => void;
  formatDate: (dateString: string) => string;
  buttonRef?: React.RefObject<HTMLElement | null>;
}

const AllSimulationsCard: React.FC<AllSimulationsCardProps> = ({
  simulations,
  isLoading,
  onClose,
  onSelectSimulation,
  formatDate,
  buttonRef,
}) => {
  const { clients, currentSimulation } = useSmsSimulatorContext();

  const cardRef = useRef<HTMLDivElement>(null);
  const [selectedClientFilter, setSelectedClientFilter] = useState<{
    label: string;
    value: string;
  } | null>(null);

  const sortedSimulations = [...simulations].sort(
    (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime(),
  );

  const clientFilterOptions = [
    { label: "All clients", value: "" },
    ...clients.filter((client) => simulations.some((sim) => sim.clientId === client.value)),
  ];

  const filteredSimulations = selectedClientFilter?.value
    ? sortedSimulations.filter((sim) => sim.clientId === selectedClientFilter.value)
    : sortedSimulations;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent | globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !cardRef.current?.contains(event.target as Node) &&
        !buttonRef?.current?.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, handleKeyDown, buttonRef]);

  return (
    <div
      className="fixed top-44 right-8 bottom-8 w-96 bg-white border border-gray-200 rounded-lg shadow-lg z-[1] overflow-hidden"
      ref={cardRef}>
      <div className="flex flex-col h-full py-4">
        <div className="flex justify-between items-center px-4">
          <h2 className="text-lg font-bold text-gray-900">All simulations</h2>
          <IconButton
            onClick={onClose}
            variant="text"
            size="small"
            color="inherit"
            aria-label="Close simulations">
            <Icons.Close />
          </IconButton>
        </div>

        <div className="p-4 border-b border-gray-200 text-neutrals-500">
          <Select
            selectName="clientFilter"
            options={clientFilterOptions}
            placeholder="Filter by client..."
            ariaLabel="Filter simulations by client"
            value={selectedClientFilter}
            disabled={isLoading}
            onChange={(selectedOption: any) => setSelectedClientFilter(selectedOption)}
          />
        </div>

        <div className="flex-1 overflow-y-auto relative">
          {isLoading ? (
            <TableLoader isLoading={true} />
          ) : filteredSimulations.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <p>No simulations found</p>
            </div>
          ) : (
            <div role="listbox" aria-label="Simulations list">
              {filteredSimulations.map((simulation) => {
                const isCurrentSimulation = currentSimulation?.id === simulation.id;
                return (
                  <button
                    key={simulation.id}
                    className={twMerge(
                      "w-full p-4 transition-colors cursor-pointer border-b border-gray-200 text-left focus:outline-none",
                      isCurrentSimulation
                        ? "bg-aqua-100 border-l-4 border-l-aqua-500"
                        : "hover:bg-aqua-50 focus:bg-aqua-50",
                    )}
                    onClick={() => onSelectSimulation(simulation)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelectSimulation(simulation);
                      }
                    }}
                    role="option"
                    aria-selected={isCurrentSimulation}
                    tabIndex={0}>
                    <div className="flex flex-col">
                      <div className="flex flex-col space-y-0.5 mb-2">
                        <h3 className="font-bold text-sm text-gray-900">
                          {clients.find((option) => option.value === simulation.clientId)?.label ||
                            "Unknown Client"}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {formatDate(simulation.createdDate)}
                        </p>
                        <span className="text-sm text-gray-600">{simulation.createdBy}</span>
                      </div>
                      <div className="text-sm text-gray-900">
                        {simulation.testSmsText.length > 100
                          ? simulation.testSmsText.substring(0, 100) + "..."
                          : simulation.testSmsText}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllSimulationsCard;
